// web/src/services/dashboardService.js
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase/firebase";
import { normalizeTimestamp } from "../utils/dateUtils";

/* ============================================================
   🔥 FUNÇÃO PRINCIPAL — Retorna TODAS as métricas reais
   ============================================================ */
export async function getDashboardMetrics() {
  const [
    totalUsers,
    activeUsersToday,
    completedProfilesPercent,
    genderDistribution,
    ageDistribution,
    citiesCount,
    plansDistribution,
    iaSessions24h,
    matchesWeek,
    premiumUsers,
    revenue,
    planTotals,
  ] = await Promise.all([
    getTotalUsers(),
    getActiveUsersToday(),
    getCompletedProfilesPercent(),
    getGenderDistribution(),
    getAgeDistribution(),
    getCitiesCount(),
    getPlansDistribution(), // ✅ agora retorna free/plus/premium/diamond
    getIaSessions24h(),
    getMatchesWeek(),
    getPremiumUsers(),
    getRevenueEstimates(),
    getPlanTotalsAndRevenueBreakdown(),
  ]);

  return {
    totalUsers,
    activeUsersToday,
    completedProfilesPercent,
    genderDistribution,
    ageDistribution,
    citiesCount,
    plansDistribution,
    iaSessions24h,
    matchesWeek,
    premiumUsers,

    // (mantido)
    monthlyRevenue: revenue.monthly,
    annualRevenue: revenue.annual,

    // ✅ Totais por plano (cards do topo)
    plusUsers: planTotals.plusUsers,
    premiumPlanUsers: planTotals.premiumUsers,
    diamondUsers: planTotals.diamondUsers,

    // ✅ Receita mensal por plano
    plusRevenueMonthly: planTotals.plusRevenueMonthly,
    premiumRevenueMonthly: planTotals.premiumRevenueMonthly,
    diamondRevenueMonthly: planTotals.diamondRevenueMonthly,

    // ✅ Receita mensal total
    estimatedRevenueTotalMonthly: planTotals.estimatedRevenueTotalMonthly,
  };
}

/* ============================================================
   🔢 TOTAL DE USUÁRIOS
   ============================================================ */
export async function getTotalUsers() {
  const snap = await getDocs(collection(db, "users"));
  return snap.size;
}

/* ============================================================
   🔥 USUÁRIOS ATIVOS NAS ÚLTIMAS 24H
   ============================================================ */
export async function getActiveUsersToday() {
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const usersSnap = await getDocs(collection(db, "users"));

  let activeCount = 0;

  usersSnap.forEach((doc) => {
    const u = doc.data();
    const lastActive = normalizeTimestamp(u.lastActiveAt);

    if (lastActive && lastActive >= last24h) {
      activeCount++;
    }
  });

  return activeCount;
}

/* ============================================================
   🧩 PERFIS COMPLETOS (%)
   ============================================================ */
export async function getCompletedProfilesPercent() {
  const usersSnap = await getDocs(collection(db, "users"));
  let completed = 0;

  usersSnap.forEach((doc) => {
    const u = doc.data();
    if (
      u.avatarUrls?.length > 0 &&
      u.bio?.length > 3 &&
      u.gender &&
      u.searchGender
    ) {
      completed++;
    }
  });

  return usersSnap.size > 0
    ? Math.round((completed / usersSnap.size) * 100)
    : 0;
}

/* ============================================================
   ⚧ DISTRIBUIÇÃO POR GÊNERO
   ============================================================ */
export async function getGenderDistribution() {
  const snap = await getDocs(collection(db, "users"));

  let female = 0;
  let male = 0;
  let other = 0;

  snap.forEach((doc) => {
    const g = (doc.data().gender || "").toLowerCase();

    if (g === "feminino") female++;
    else if (g === "masculino") male++;
    else other++;
  });

  return { female, male, other };
}

/* ============================================================
   🎂 DISTRIBUIÇÃO POR FAIXA ETÁRIA
   ============================================================ */
export async function getAgeDistribution() {
  const snap = await getDocs(collection(db, "users"));

  const ranges = { "18-24": 0, "25-34": 0, "35-44": 0, "45+": 0 };
  const currentYear = new Date().getFullYear();

  snap.forEach((doc) => {
    const birth = doc.data().birthDate;
    if (!birth) return;

    let birthYear;

    if (birth?.seconds) {
      birthYear = new Date(birth.seconds * 1000).getFullYear();
    } else {
      const d = new Date(birth);
      if (isNaN(d.getTime())) return;
      birthYear = d.getFullYear();
    }

    const age = currentYear - birthYear;

    if (age <= 24) ranges["18-24"]++;
    else if (age <= 34) ranges["25-34"]++;
    else if (age <= 44) ranges["35-44"]++;
    else ranges["45+"]++;
  });

  return ranges;
}

/* ============================================================
   📍 CONTAGEM DE CIDADES (CORRIGIDO)
   - Normaliza cidade pra não duplicar: "Curitiba", "curitiba", "Curitiba "
   - Ignora vazios
   ============================================================ */
export async function getCitiesCount() {
  const snap = await getDocs(collection(db, "users"));

  const set = new Set();

  snap.forEach((d) => {
    const raw = d.data()?.city;
    const city = String(raw || "")
      .trim()
      .replace(/\s+/g, " ")
      .toLowerCase();

    if (city) set.add(city);
  });

  return set.size;
}

/* ============================================================
   ✅ NORMALIZAÇÃO DE PLANO (reaproveitada)
   plus | premium | diamond | free
   ============================================================ */
function normalizePlanFromUser(user) {
  const rawPlan = String(user?.plan || "").toLowerCase().trim();
  const planoNome = String(user?.planoNome || "").toLowerCase().trim();

  const text = `${rawPlan} ${planoNome}`.trim();

  // Diamond
  if (
    text.includes("diamond") ||
    text.includes("anual") ||
    text.includes("annual") ||
    text.includes("vip")
  ) {
    return "diamond";
  }

  // Premium
  if (
    text.includes("premium") ||
    text.includes("mensal") ||
    text.includes("monthly")
  ) {
    return "premium";
  }

  // Plus (ou starter antigo)
  if (
    text.includes("plus") ||
    text.includes("starter") ||
    text.includes("basic")
  ) {
    return "plus";
  }

  return "free";
}

/* ============================================================
   💳 DISTRIBUIÇÃO DE PLANOS (AGORA CORRETA)
   Retorna contagens: { free, plus, premium, diamond }
   ============================================================ */
export async function getPlansDistribution() {
  const snap = await getDocs(collection(db, "users"));

  let free = 0;
  let plus = 0;
  let premium = 0;
  let diamond = 0;

  snap.forEach((doc) => {
    const u = doc.data();
    const plan = normalizePlanFromUser(u);

    if (plan === "free") free++;
    else if (plan === "plus") plus++;
    else if (plan === "premium") premium++;
    else if (plan === "diamond") diamond++;
  });

  return { free, plus, premium, diamond };
}

/* ============================================================
   🤖 SESSÕES IA NAS ÚLTIMAS 24H
   ============================================================ */
export async function getIaSessions24h() {
  const now = new Date();
  const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const sessSnap = await getDocs(collection(db, "ai_sessoes"));

  let count = 0;

  sessSnap.forEach((doc) => {
    const s = doc.data();
    const created = normalizeTimestamp(s.createdAt);

    if (created && created >= last24h) count++;
  });

  return count;
}

/* ============================================================
   ❤️ MATCHES NA SEMANA
   ============================================================ */
export async function getMatchesWeek() {
  const now = new Date();
  const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const matchesSnap = await getDocs(collection(db, "matches"));

  let count = 0;

  matchesSnap.forEach((doc) => {
    const m = doc.data();
    const created = normalizeTimestamp(m.createdAt);

    if (created && created >= last7d) count++;
  });

  return count;
}

/* ============================================================
   🌟 USUÁRIOS PREMIUM (mantido)
   ============================================================ */
export async function getPremiumUsers() {
  const snap = await getDocs(collection(db, "users"));

  let count = 0;

  snap.forEach((doc) => {
    const plan = (doc.data().plan || "").toLowerCase();

    if (
      ["starter", "mensal", "monthly", "premium", "anual", "annual"].includes(
        plan
      )
    ) {
      count++;
    }
  });

  return count;
}

/* ============================================================
   💰 ESTIMATIVA DE RECEITA (mantido)
   ============================================================ */
export async function getRevenueEstimates() {
  const snap = await getDocs(collection(db, "users"));

  let starter = 0;
  let monthly = 0;
  let annual = 0;

  snap.forEach((doc) => {
    const plan = (doc.data().plan || "").toLowerCase();

    if (plan === "starter") starter++;
    if (plan === "mensal" || plan === "monthly") monthly++;
    if (plan === "anual" || plan === "annual") annual++;
  });

  const monthlyRevenue = starter * 14.9 + monthly * 59.9 + annual * (349 / 12);

  const annualRevenue =
    starter * 14.9 * 12 + monthly * 59.9 * 12 + annual * 349;

  return { monthly: monthlyRevenue, annual: annualRevenue };
}

/* ============================================================
   ✅ Totais por plano + Receita mensal por plano (mantido)
   ============================================================ */
export async function getPlanTotalsAndRevenueBreakdown() {
  const snap = await getDocs(collection(db, "users"));

  let plusUsers = 0;
  let premiumUsers = 0;
  let diamondUsers = 0;

  snap.forEach((doc) => {
    const u = doc.data();
    const p = normalizePlanFromUser(u);

    if (p === "plus") plusUsers++;
    else if (p === "premium") premiumUsers++;
    else if (p === "diamond") diamondUsers++;
  });

  // ✅ Preços (ajuste se necessário)
  const PRICE_PLUS = 14.9;
  const PRICE_PREMIUM = 59.9;
  const PRICE_DIAMOND_ANNUAL = 349;

  const plusRevenueMonthly = plusUsers * PRICE_PLUS;
  const premiumRevenueMonthly = premiumUsers * PRICE_PREMIUM;
  const diamondRevenueMonthly = diamondUsers * (PRICE_DIAMOND_ANNUAL / 12);

  const estimatedRevenueTotalMonthly =
    plusRevenueMonthly + premiumRevenueMonthly + diamondRevenueMonthly;

  return {
    plusUsers,
    premiumUsers,
    diamondUsers,
    plusRevenueMonthly,
    premiumRevenueMonthly,
    diamondRevenueMonthly,
    estimatedRevenueTotalMonthly,
  };
}
